import React from 'react';
import { Group, NumberInput, SegmentedControl, Select, Stack, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { ConfigFormValues, operatorOptions, trendTypeOptions, valueModeOptions } from './alertConfigurationConstants.ts';

interface Props {
    form: UseFormReturnType<ConfigFormValues>;
    physiologicalUnits: { value: string; label: string }[];
    evaluationMode: string;
}

const PhysiologicalConditionFields: React.FC<Props> = ({ form, physiologicalUnits, evaluationMode }) => {
    const { t } = useTranslation();
    const isTrend = evaluationMode === 'trend';
    const needsPercentageOption = form.values.conditionTrendType === 'INCREASE' || form.values.conditionTrendType === 'DECREASE';
    // Counts (awakenings, nightmares, steps) carry a deliberately blank-named unit, so a read-only
    // "Unit" field would render empty. Treat that as having no unit to show at all.
    const hasSingleNamedUnit =
        physiologicalUnits.length === 1 && (physiologicalUnits[0].label ?? '').trim() !== '';

    if (isTrend) {
        return (
            <Stack gap="xs">
                <Group grow align="flex-start">
                    <Select
                        label={t('alertRule.trend.trendType')}
                        placeholder={t('alertRule.trend.trendTypePlaceholder')}
                        data={trendTypeOptions(t)}
                        {...form.getInputProps('conditionTrendType')}
                    />
                    <NumberInput
                        label={t('alertRule.trend.sampleCount')}
                        min={2}
                        max={100}
                        {...form.getInputProps('conditionSampleCount')}
                    />
                </Group>
                <Group grow align="flex-start">
                    <NumberInput
                        label={t('alertRule.trend.value')}
                        placeholder="0"
                        decimalScale={2}
                        {...form.getInputProps('conditionTrendValue')}
                    />
                    {needsPercentageOption && (
                        <SegmentedControl
                            data={valueModeOptions(t)}
                            {...form.getInputProps('conditionValueMode')}
                            mt={25}
                        />
                    )}
                    {physiologicalUnits.length > 1 ? (
                        <Select
                            label={t('alertRule.form.unit')}
                            data={physiologicalUnits}
                            value={form.values.conditionUnitId?.toString() ?? null}
                            onChange={(val) => form.setFieldValue('conditionUnitId', val ? Number(val) : null)}
                        />
                    ) : hasSingleNamedUnit ? (
                        <TextInput
                            label={t('alertRule.form.unit')}
                            value={physiologicalUnits[0].label}
                            readOnly
                        />
                    ) : null}
                </Group>
            </Stack>
        );
    }

    return (
        <Group grow align="flex-start">
            <Select
                label={t('alertRule.form.operator')}
                data={operatorOptions}
                {...form.getInputProps('conditionOperator')}
            />
            <NumberInput
                label={t('alertRule.form.value')}
                placeholder="0"
                decimalScale={2}
                {...form.getInputProps('conditionThreshold')}
            />
            {physiologicalUnits.length > 1 ? (
                <Select
                    label={t('alertRule.form.unit')}
                    data={physiologicalUnits}
                    value={form.values.conditionUnitId?.toString() ?? null}
                    onChange={(val) => form.setFieldValue('conditionUnitId', val ? Number(val) : null)}
                />
            ) : hasSingleNamedUnit ? (
                <TextInput
                    label={t('alertRule.form.unit')}
                    value={physiologicalUnits[0].label}
                    readOnly
                />
            ) : null}
        </Group>
    );
};

export default PhysiologicalConditionFields;
